import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Tooltip } from '@blueprintjs/core';
import React, { useEffect, useState } from 'react';

import {
	FILE_SIZE_CACHE,
	FILE_SIZE_REQUEST,
	MAX_CONCURRENT_FILE_SIZE_REQUESTS,
	MEDIA_IMAGE_ELEMENT_TYPES,
} from '../constants';

interface MediaMetricsPillsProps {
	store: any;
	t: (key: string) => string;
}

interface MediaMetrics {
	totalSizeBytes: number | null;
	totalVideoSizeBytes: number | null;
	totalVideoSeconds: number | null;
	hasVideo: boolean;
}

interface MediaRef {
	src: string;
	kind: 'video' | 'image';
	durationSeconds: number | null;
}

const isNonNegativeFinite = (value: any): value is number => Number.isFinite(value) && value >= 0;

const fetchFileSize = async (url: string, init: RequestInit = {}): Promise<Response> => {
    const opts = { mode: 'cors' as RequestMode, ...init };
    return fetch(url, { ...opts, credentials: 'omit' });
};

const parseSizeBytesFromResponse = (resp: Response | null): number | null => {
    const headers = resp?.headers;
    if (!headers) return null;

    const rangeTotal = Number(headers.get('content-range')?.match(/\/(\d+)\s*$/)?.[1]);
    if (isNonNegativeFinite(rangeTotal)) return rangeTotal;

    const h = headers.get('content-length');
    const contentLength = h == null ? null : Number(h);
    return isNonNegativeFinite(contentLength) ? contentLength : null;
};

const getFileSizeBytesFromUrl = async (src: string): Promise<number | null> => {
    if (typeof src !== 'string' || !src) return null;

    try {
        const response = await fetchFileSize(src, FILE_SIZE_REQUEST);
        const size = response?.ok ? parseSizeBytesFromResponse(response) : null;
        return isNonNegativeFinite(size) ? size : null;
    } catch (error) {
        // Ignore
    }

    return null;
};

const getFileSizeWithCache = (src: string): Promise<number | null> => {
    if (!src) return Promise.resolve(null);
    if (FILE_SIZE_CACHE.has(src)) return FILE_SIZE_CACHE.get(src)!;

    const promise = getFileSizeBytesFromUrl(src);
    FILE_SIZE_CACHE.set(src, promise);
    return promise;
};

const mapWithConcurrency = async <T, R>(
    items: T[],
    mapper: (item: T, index: number) => Promise<R>,
    concurrency: number = MAX_CONCURRENT_FILE_SIZE_REQUESTS,
): Promise<R[]> => {
    if (!Array.isArray(items) || items.length === 0) return [];

    if (!Number.isFinite(concurrency) || concurrency <= 0) {
        return Promise.all(items.map((item, index) => mapper(item, index)));
    }

    const results = new Array<R>(items.length);

    for (let i = 0; i < items.length; i += concurrency) {
        const batch = items.slice(i, i + concurrency);
        const batchResults = await Promise.all(
            batch.map((item, index) => mapper(item, i + index)),
        );

        for (let j = 0; j < batchResults.length; j += 1) {
            results[i + j] = batchResults[j];
        }
    }

    return results;
};

const formatFileSize = (bytes: number | null): string => {
    if (!Number.isFinite(bytes)) return '—';
    const value = Math.max(0, bytes as number);
    if (value < 1024) return `${Math.round(value)} B`;
    const units = ['KB', 'MB', 'GB'];
    const i = Math.min(2, Math.floor(Math.log(value) / Math.log(1024)) - 1);
    return `${(value / 1024 ** (i + 1)).toFixed(1).replace(/\.0$/, '')} ${units[i]}`;
};

const formatDuration = (seconds: number | null): string => {
    if (!Number.isFinite(seconds) || (seconds as number) < 0) return '—';
    const total = Math.floor(seconds as number);
    const m = Math.floor(total / 60);
    const s = String(total % 60).padStart(2, '0');
    return m ? `${m}m ${s} S` : `${s} S`;
};

const getDurationSecondsFromElement = (element: any): number | null => {
    if (!element) return null;
    const raw = element.duration;
    if (!Number.isFinite(raw) || raw < 0) return null;
    return raw > 1000 ? raw / 1000 : raw;
};

const getMediaRefFromElement = (element: any): MediaRef | null => {
    if (!element) return null;
    const src = element.src;
    if (typeof src !== 'string' || !src) return null;
    const isVideo = element.type === 'video';
    const isImage = MEDIA_IMAGE_ELEMENT_TYPES.includes(element.type);
    if (!isVideo && !isImage) return null;

    return {
        src,
        kind: isVideo ? 'video' : 'image',
        durationSeconds: isVideo ? getDurationSecondsFromElement(element) : null,
    };
};

const getTemplateMediaRefs = (store: any): MediaRef[] => {
    if (!store?.pages) return [];
    const refs: MediaRef[] = [];

    const visit = (node: any): void => {
        if (!node) return;
        const ref = getMediaRefFromElement(node);
        if (ref) refs.push(ref);
        node.children?.forEach?.(visit);
    };

    store.pages.forEach((page: any) => page?.children?.forEach?.(visit));
    return refs;
};

const getMediaSignature = (refs: MediaRef[]): string =>
    refs
        .map((ref) => {
            if (ref.kind !== 'video') return `image:${ref.src}`;
            const durationToken = Number.isFinite(ref.durationSeconds)
                ? Math.round((ref.durationSeconds as number) * 1000)
                : '';
            return `video:${ref.src}:${durationToken}`;
        })
        .sort()
        .join('|');

const getMetricsFromRefs = async (refs: MediaRef[]): Promise<MediaMetrics> => {
    const videoRefs = refs.filter((ref) => ref.kind === 'video');
    const sizes = await mapWithConcurrency(
        refs,
        (ref) => getFileSizeWithCache(ref.src),
        MAX_CONCURRENT_FILE_SIZE_REQUESTS,
    );

    const hasResolvedSize = sizes.some((value) => Number.isFinite(value));

    return {
        totalSizeBytes: hasResolvedSize
            ? sizes.reduce(
                (sum, value) => sum + (Number.isFinite(value) ? (value as number) : 0),
                0,
            )
            : null,
        totalVideoSizeBytes: videoRefs.length > 0
            ? refs.reduce(
                (sum, ref, index) => {
                    if (ref.kind !== 'video') return sum;
                    const value = sizes[index];
                    return sum + (Number.isFinite(value) ? (value as number) : 0);
                },
                0,
            )
            : null,
        totalVideoSeconds: videoRefs.length > 0
            ? videoRefs.reduce(
                (sum, ref) => sum + (Number.isFinite(ref.durationSeconds) ? (ref.durationSeconds as number) : 0),
                0,
            )
            : null,
        hasVideo: videoRefs.length > 0,
    };
};

export const getMediaMetricsFromStore = async (store: any): Promise<MediaMetrics> => {
    const refs = getTemplateMediaRefs(store);
    return getMetricsFromRefs(refs);
};

const MediaMetricsPills: React.FC<MediaMetricsPillsProps> = ({ store, t }) => {
    const [metrics, setMetrics] = useState<MediaMetrics>({
        totalSizeBytes: null,
        totalVideoSizeBytes: null,
        totalVideoSeconds: null,
        hasVideo: false,
    });

    useEffect(() => {
        if (!store) return;

        let isDisposed = false;
        let requestId = 0;

        const updateMetrics = async () => {
            const currentRequestId = ++requestId;
            const nextMetrics = await getMediaMetricsFromStore(store);

            if (isDisposed || currentRequestId !== requestId) return;
            setMetrics(nextMetrics);
        };

        const dispose = reaction(
            () => getMediaSignature(getTemplateMediaRefs(store)),
            () => {
                void updateMetrics();
            },
            { fireImmediately: true },
        );

        return () => {
            isDisposed = true;
            dispose();
        };
    }, [store]);

    if ((store?.selectedElements ?? []).length > 0) return null;

    return (
        <div className="d-inline-flex align-items-center gap-2 media-metrics-container">
            <Tooltip content={t('fileSizeTooltip')} position="bottom">
                <span className="rounded-1 p-2 px-3 file-size-span media-metrics-pill">
                    <span className="file-size-text media-metrics-text fw-normal media-metrics-label">
                        {t('fileSize')}:
                    </span>{' '}
                    <span className="media-metrics-text fw-semibold media-metrics-value">
                        {formatFileSize(metrics.totalSizeBytes)}
                    </span>
                </span>
            </Tooltip>
            {metrics.hasVideo && (
                <Tooltip content={t('videoLengthTooltip')} position="bottom">
                    <span className="p-2 px-3 rounded-1 video-length-span media-metrics-pill">
                        <span className="video-length-text media-metrics-text fw-normal media-metrics-label">
                            {t('videoLength')}:
                        </span>{' '}
                        <span className="media-metrics-text fw-semibold media-metrics-value">
                            {formatDuration(metrics.totalVideoSeconds)}
                        </span>
                    </span>
                </Tooltip>
            )}
        </div>
    );
};

export default observer(MediaMetricsPills);
